import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  BriefcaseIcon,
  FlagIcon
} from '@heroicons/react/24/outline';
import ProjectForm from './forms/ProjectForm';
import { apiClient } from '../api/apiConfig';
import { Project } from '../types/project';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface SortField {
  field: 'name' | 'status' | 'created_at';
}

type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'paused' | 'completed' | 'archived';

const ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [businessFilter, setBusinessFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState<string>('all');

  // Sorting
  const [sortField, setSortField] = useState<SortField['field']>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, tasksRes, goalsRes, businessesRes, categoriesRes] = await Promise.all([
        apiClient.get(`/api/projects/${searchQuery ? `?search=${searchQuery}` : ''}`),
        apiClient.get('/api/tasks/'),
        apiClient.get('/api/goals/'),
        apiClient.get('/api/businesses/'),
        apiClient.get('/api/categories/')
      ]);

      // Combine projects with their tasks and goals
      const projectsWithRelations = projectsRes.data.map((project: Project) => ({
        ...project,
        tasks: tasksRes.data.filter((task: any) => task.project?.id === project.id),
        goals: goalsRes.data.filter((goal: any) => goal.project?.id === project.id)
      }));

      setProjects(projectsWithRelations);
      setBusinesses(businessesRes.data);
      setCategories(categoriesRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field: SortField['field']) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField['field']) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUpIcon style={{ width: '16px', height: '16px' }} />
    ) : (
      <ChevronDownIcon style={{ width: '16px', height: '16px' }} />
    );
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'active':
        return { backgroundColor: '#D1FAE5', color: '#065F46' };
      case 'paused':
        return { backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'completed':
        return { backgroundColor: '#DBEAFE', color: '#1E40AF' };
      default:
        return { backgroundColor: COLORS.bg.hover, color: COLORS.text.secondary };
    }
  };

  const toggleExpand = (projectId: number) => {
    setExpandedProjectId(expandedProjectId === projectId ? null : projectId);
  };

  const filteredAndSortedProjects = useMemo(() => {
    let filtered = [...projects];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    // Apply business filter
    if (businessFilter !== 'all') {
      filtered = filtered.filter(project => project.business?.id === parseInt(businessFilter));
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(project =>
        project.categories?.some((cat: any) => cat.id === parseInt(categoryFilter))
      );
    }

    // Apply project type filter
    if (projectTypeFilter !== 'all') {
      filtered = filtered.filter(project => project.project_type === projectTypeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let valA: any;
      let valB: any;

      switch (sortField) {
        case 'name':
          valA = a.name.toLowerCase();
          valB = b.name.toLowerCase();
          break;
        case 'status':
          valA = a.status;
          valB = b.status;
          break;
        case 'created_at':
          valA = a.created_at ? new Date(a.created_at).getTime() : 0;
          valB = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [projects, statusFilter, businessFilter, categoryFilter, projectTypeFilter, sortField, sortDirection]);

  const handleEdit = (project: Project) => {
    setEditProject(project);
    setIsEditModalOpen(true);
  };

  const handleDeleteProject = async (projectId: number) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await apiClient.delete(`/api/projects/${projectId}/`);

      // Remove project from local state
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  // Shared styles
  const selectStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    paddingLeft: SPACING[12],
    paddingRight: SPACING[32],
    paddingTop: SPACING[8],
    paddingBottom: SPACING[8],
    border: `1px solid ${COLORS.border.default}`,
    borderRadius: RADIUS,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    outline: 'none',
    backgroundColor: COLORS.bg.panel,
  };

  const sortButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.text.secondary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
      }}>
        <ArrowPathIcon style={{
          width: '32px',
          height: '32px',
          color: COLORS.text.tertiary,
          animation: 'spin 1s linear infinite',
        }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: SPACING[12],
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.status.error,
          backgroundColor: '#FEF2F2',
          borderRadius: RADIUS,
          border: `1px solid #FECACA`,
        }}>
          <ExclamationTriangleIcon style={{ width: '20px', height: '20px', marginRight: SPACING[8] }} />
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
        {/* Search Bar */}
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            paddingLeft: SPACING[12],
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
          }}>
            <MagnifyingGlassIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
          </div>
          <input
            type="text"
            placeholder="Search projects..."
            style={{
              display: 'block',
              width: '100%',
              paddingLeft: SPACING[40],
              paddingRight: SPACING[12],
              paddingTop: SPACING[8],
              paddingBottom: SPACING[8],
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              lineHeight: '20px',
              backgroundColor: COLORS.bg.panel,
              fontSize: TYPOGRAPHY.size.sm,
              fontFamily: TYPOGRAPHY.body.fontFamily,
              outline: 'none',
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Filter Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: SPACING[12],
        }}>
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            style={selectStyle}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>

          {/* Project Type Filter */}
          <select
            value={projectTypeFilter}
            onChange={(e) => setProjectTypeFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All Types</option>
            <option value="build">Build (0-1)</option>
            <option value="run">Run (Ongoing)</option>
          </select>

          {/* Business Filter */}
          <select
            value={businessFilter}
            onChange={(e) => setBusinessFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All Businesses</option>
            {businesses.map(business => (
              <option key={business.id} value={business.id}>{business.name}</option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </div>

        {/* Sort Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <button
            onClick={() => handleSort('name')}
            style={sortButtonStyle}
          >
            Name {renderSortIcon('name')}
          </button>
          <button
            onClick={() => handleSort('status')}
            style={sortButtonStyle}
          >
            Status {renderSortIcon('status')}
          </button>
          <button
            onClick={() => handleSort('created_at')}
            style={sortButtonStyle}
          >
            Created {renderSortIcon('created_at')}
          </button>
        </div>
      </div>

      {/* Project List */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[16],
      }}>
        {filteredAndSortedProjects.map((project) => (
          <div
            key={project.id}
            style={{
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            {/* Header Row */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: SPACING[8],
                padding: SPACING[12],
                cursor: 'pointer',
                flex: 1,
              }}
              onClick={() => toggleExpand(project.id)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = COLORS.bg.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <div style={{
                  paddingLeft: SPACING[8],
                  paddingRight: SPACING[8],
                  paddingTop: '2px',
                  paddingBottom: '2px',
                  borderRadius: '9999px',
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  ...getStatusStyle(project.status || 'active'),
                }}>
                  {project.status || 'active'}
                </div>
                <h3 style={{
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.semibold,
                  color: COLORS.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  margin: 0,
                }}>
                  {project.name}
                </h3>
                <ChevronDownIcon
                  style={{
                    width: '16px',
                    height: '16px',
                    color: COLORS.text.tertiary,
                    transition: 'transform 0.2s',
                    transform: expandedProjectId === project.id ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING[16],
                marginTop: SPACING[4],
                fontSize: TYPOGRAPHY.size.xs,
                color: COLORS.text.secondary,
              }}>
                {project.business && (
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <BriefcaseIcon style={{ width: '14px', height: '14px', marginRight: '6px', color: COLORS.text.tertiary }} />
                    {project.business.name}
                  </span>
                )}
                {project.goal && (
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <FlagIcon style={{ width: '14px', height: '14px', marginRight: '6px', color: COLORS.text.tertiary }} />
                    {project.goal.title}
                  </span>
                )}
              </div>
            </div>

            {/* Expanded Content */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: SPACING[16],
                transition: 'all 0.2s',
                opacity: expandedProjectId === project.id ? 1 : 0,
                maxHeight: expandedProjectId === project.id ? '500px' : '0px',
                padding: expandedProjectId === project.id ? SPACING[16] : '0px',
                borderTop: expandedProjectId === project.id ? `1px solid ${COLORS.bg.hover}` : 'none',
                overflow: 'hidden',
              }}
            >
              {/* Description */}
              {project.description && (
                <div>
                  <h4 style={{
                    fontSize: TYPOGRAPHY.size.xs,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.secondary,
                    marginBottom: SPACING[4],
                    marginTop: 0,
                  }}>Description</h4>
                  <p style={{
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.text.primary,
                    margin: 0,
                  }}>{project.description}</p>
                </div>
              )}

              {/* Project Details Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: SPACING[16],
              }}>
                {/* Goal */}
                {project.goal && (
                  <div>
                    <h4 style={{
                      fontSize: TYPOGRAPHY.size.xs,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      marginBottom: SPACING[4],
                      marginTop: 0,
                    }}>Goal</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      <FlagIcon style={{ width: '16px', height: '16px', color: COLORS.text.tertiary }} />
                      <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.primary }}>{project.goal.title}</span>
                    </div>
                  </div>
                )}

                {/* Business */}
                <div>
                  <h4 style={{
                    fontSize: TYPOGRAPHY.size.xs,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.secondary,
                    marginBottom: SPACING[4],
                    marginTop: 0,
                  }}>Business</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                    <BriefcaseIcon style={{ width: '16px', height: '16px', color: COLORS.text.tertiary }} />
                    <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.primary }}>{project.business?.name || 'No business linked'}</span>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              {project.tasks && project.tasks.length > 0 && (
                <div>
                  <h4 style={{
                    fontSize: TYPOGRAPHY.size.xs,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.secondary,
                    marginBottom: SPACING[8],
                    marginTop: 0,
                  }}>Progress</h4>
                  <div style={{
                    width: '100%',
                    backgroundColor: COLORS.border.default,
                    borderRadius: '9999px',
                    height: '8px',
                  }}>
                    <div
                      style={{
                        backgroundColor: COLORS.accent.default,
                        height: '8px',
                        borderRadius: '9999px',
                        transition: 'all 0.3s',
                        width: `${(project.tasks.filter(t => t.is_done).length / project.tasks.length) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: TYPOGRAPHY.size.xs,
                    color: COLORS.text.secondary,
                    marginTop: SPACING[4],
                  }}>
                    <span>{project.tasks.filter(t => t.is_done).length} completed</span>
                    <span>{project.tasks.length} total tasks</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: SPACING[8],
                borderTop: `1px solid ${COLORS.bg.hover}`,
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/projects/${project.id}`);
                  }}
                  style={{
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.accent.default,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = COLORS.accent.hover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = COLORS.accent.default;
                  }}
                >
                  View Details
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(project);
                    }}
                    style={{
                      padding: SPACING[4],
                      color: COLORS.text.tertiary,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    title="Edit project"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = COLORS.text.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = COLORS.text.tertiary;
                    }}
                  >
                    <PencilIcon style={{ width: '16px', height: '16px' }} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(project.id);
                    }}
                    style={{
                      padding: SPACING[4],
                      color: COLORS.text.tertiary,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    title="Delete project"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = COLORS.status.error;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = COLORS.text.tertiary;
                    }}
                  >
                    <TrashIcon style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && editProject && (
        <ProjectForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditProject(null);
          }}
          onProjectUpdated={() => {
            setIsEditModalOpen(false);
            setEditProject(null);
            // Refetch projects to get updated data
            fetchData();
          }}
          initialProject={editProject}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default ProjectList;
